/**
 * Helpers shared by /login and AuthModal. Both forms keep the addresses that
 * signed up in this browser (localStorage `emailHistory`) and refuse a fourth
 * new one: a device limit against account farms that sits next to the
 * server-side checks in /api/validate-email and /api/user-created.
 */
export const EMAIL_HISTORY_LIMIT = 3;

export const normalizeEmail = (value: string) => value.trim().toLowerCase();

export const buildUniqueEmailHistory = (entries: unknown): string[] => {
  if (!Array.isArray(entries)) return [];

  return entries.reduce<string[]>((acc, entry) => {
    if (typeof entry !== 'string') return acc;
    const trimmed = entry.trim();
    if (!trimmed) return acc;
    const normalized = normalizeEmail(trimmed);
    if (
      !acc.some((storedEmail) => normalizeEmail(storedEmail) === normalized)
    ) {
      acc.push(trimmed);
    }
    return acc;
  }, []);
};

/**
 * Known inbox providers: the "Open <inbox>" shortcut after sign-up and the
 * Google nudge for Gmail addresses (a Gmail user can skip email confirmation
 * by continuing with Google).
 */
export const INBOX_PROVIDERS: Array<{
  name: string;
  url: string;
  domains: RegExp;
  isGoogle?: boolean;
}> = [
  {
    name: 'Gmail',
    url: 'https://mail.google.com/mail/u/0/',
    domains: /@(gmail\.com|googlemail\.com)$/i,
    isGoogle: true,
  },
  {
    name: 'Outlook',
    url: 'https://outlook.live.com/mail/0/',
    domains: /@(outlook\.|hotmail\.|live\.|msn\.com)/i,
  },
  {
    name: 'Yahoo Mail',
    url: 'https://mail.yahoo.com',
    domains: /@(yahoo\.|ymail\.com)/i,
  },
  {
    name: 'iCloud Mail',
    url: 'https://www.icloud.com/mail',
    domains: /@(icloud\.com|me\.com|mac\.com)$/i,
  },
];

export const inboxProviderFor = (address: string | null | undefined) =>
  address
    ? (INBOX_PROVIDERS.find((p) => p.domains.test(address.trim())) ?? null)
    : null;
